import { useCallback, useContext } from 'react';
import type { Route } from '@/services/routes/route.vm';
import type { Content } from '@/services/contents/content.vm';
import type { Stage } from '@/services/stages/stage.vm';
import { contentContext } from '@/context/contents/context';
import { ContentsActionTypes } from '@/context/contents/actions';

const useLearningContents = () => {
  const { contents, dispatch } = useContext(contentContext);

  const loadLearningPathContents = useCallback(
    (routeId: Route['id'], contents?: Content[]) => {
      if (contents) {
        dispatch({
          type: ContentsActionTypes.LOAD_LEARNING_PATH_CONTENTS,
          payload: { routeId, contents },
        });
        return;
      }
    },
    [dispatch],
  );

  const loadLearningContent = useCallback(
    (routeId: Route['id'], contentId: Content['id'], content: Content) => {
      dispatch({
        type: ContentsActionTypes.LOAD_LEARNING_CONTENT,
        payload: { routeId, contentId, content },
      });
    },
    [dispatch],
  );

  const getLearningPathContents = useCallback(
    (routeId: Route['id']) => {
      const learningPathContents = contents[routeId];
      return learningPathContents;
    },
    [contents],
  );

  const getLearningContent = useCallback(
    (routeId: Route['id'], contentId: Content['id']): Content | undefined => {
      const routeContents = getLearningPathContents(routeId);
      const content = routeContents?.contents?.find((content) => content.id === contentId);
      return content;
    },
    [getLearningPathContents],
  );

  const addLearningContent = useCallback(
    (content: Content) => {
      dispatch({
        type: ContentsActionTypes.ADD_CONTENT,
        payload: { routeId: content.routeId, content },
      });
    },
    [dispatch],
  );

  const updateLearningContent = useCallback(
    (routeId: Route['id'], contentId: Content['id'], content: Partial<Content>) => {
      dispatch({
        type: ContentsActionTypes.UPDATE_CONTENT,
        payload: { contentId, routeId: routeId ?? content.routeId, content },
      });
    },
    [dispatch],
  );

  const deleteLearningContent = useCallback(
    (routeId: Route['id'], contentId: Content['id']) => {
      dispatch({
        type: ContentsActionTypes.DELETE_CONTENT,
        payload: { routeId: routeId, contentId },
      });
    },
    [dispatch],
  );

  const markContentAsCompleted = useCallback(
    (routeId: Content['id'], contentId: Content['id']) => {
      updateLearningContent(routeId, contentId, { completed: true });
    },
    [updateLearningContent],
  );

  const getStageContents = useCallback(
    (routeId: Route['id'], stageId: Stage['id']) => {
      const stageContents = contents[routeId]?.stages[stageId];
      return stageContents;
    },
    [contents],
  );

  const getStageContent = useCallback(
    (routeId: Route['id'], stageId: Stage['id'], contentId: Content['id']): Content | undefined => {
      const stageContents = getStageContents(routeId, stageId);
      const content = stageContents?.find((content) => content.id === contentId);
      return content;
    },
    [getStageContents],
  );

  const loadStageContents = useCallback(
    (routeId: Route['id'], stageId: Stage['id'], contents: Content[]) => {
      if (contents) {
        dispatch({
          type: ContentsActionTypes.LOAD_STAGE_CONTENTS,
          payload: { routeId, stageId, contents },
        });
        return;
      }
    },
    [dispatch],
  );

  interface LoadStageContentParams {
    routeId: Route['id'];
    stageId: Stage['id'];
    contentId: Content['id'];
    content: Content;
  }

  const loadStageContent = useCallback(
    ({ routeId, stageId, contentId, content }: LoadStageContentParams) => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      if (!stageId) {
        throw new Error('Stage Id not provided');
      }
      if (!contentId) {
        throw new Error('Content Id not provided');
      }
      dispatch({
        type: ContentsActionTypes.LOAD_STAGE_CONTENT,
        payload: { routeId, stageId, contentId, content },
      });
    },
    [dispatch],
  );

  const addStageContent = useCallback(
    (routeId: Route['id'], stageId: Stage['id'], content: Content) => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      if (!stageId) {
        throw new Error('Stage Id not provided');
      }
      dispatch({
        type: ContentsActionTypes.ADD_STAGE_CONTENT,
        payload: { routeId, stageId, content },
      });
    },
    [dispatch],
  );

  const updateStageContent = useCallback(
    (routeId: Route['id'], stageId: Stage['id'], contentId: Content['id'], content: Partial<Content>) => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      if (!stageId) {
        throw new Error('Stage Id not provided');
      }
      if (!contentId) {
        throw new Error('Content Id not provided');
      }
      dispatch({
        type: ContentsActionTypes.UPDATE_STAGE_CONTENT,
        payload: { routeId, stageId, contentId, content },
      });
    },
    [dispatch],
  );

  const deleteStageContent = useCallback(
    (routeId: Route['id'], stageId: Stage['id'], contentId: Content['id']) => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      if (!stageId) {
        throw new Error('Stage Id not provided');
      }
      if (!contentId) {
        throw new Error('Content Id not provided');
      }
      dispatch({
        type: ContentsActionTypes.DELETE_STAGE_CONTENT,
        payload: { routeId, stageId, contentId },
      });
    },
    [dispatch],
  );

  const markStageContentAsCompleted = useCallback(
    (routeId: Content['id'], stageId: Stage['id'], contentId: Content['id']) => {
      updateStageContent(routeId, stageId, contentId, { completed: true });
    },
    [updateStageContent],
  );

  const routeContentsHasLoaded = useCallback(
    (routeId: Route['id']): boolean => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      return contents?.[routeId]?.contentsHasLoaded ?? false;
    },
    [contents],
  );

  const stageContentsHasLoaded = useCallback(
    (routeId: Route['id'], stageId: Stage['id']): boolean => {
      if (!routeId) {
        throw new Error('Route Id not provided');
      }
      return contents?.[routeId]?.stagesHasLoaded?.[stageId] ?? false;
    },
    [contents],
  );

  return {
    // Route Contents,
    contentsMap: contents,
    loadLearningPathContents,
    loadLearningContent,
    getLearningContent,
    getLearningPathContents,
    addLearningContent,
    updateLearningContent,
    deleteLearningContent,
    markContentAsCompleted,
    routeContentsHasLoaded,

    // Stage Contents,
    getStageContents,
    getStageContent,
    loadStageContents,
    loadStageContent,
    addStageContent,
    updateStageContent,
    deleteStageContent,
    markStageContentAsCompleted,
    stageContentsHasLoaded,
  };
};

export default useLearningContents;
